'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Image from 'next/image';
import TrustpilotWidget from '@/components/TrustpilotWidget';

const entitlements = [
  { emoji: '🚗', text: 'Full repair cost paid by the at-fault insurer' },
  { emoji: '🔑', text: 'Courtesy car while your vehicle is being repaired' },
  { emoji: '💷', text: 'Recovery of your excess (if already paid)' },
  { emoji: '📝', text: 'Market value payout if your car is written off' },
  { emoji: '🚛', text: 'Recovery of towing, storage, and other related expenses' },
];

const steps = [
  {
    num: '01',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
      </svg>
    ),
    title: 'Contact Us',
    text: 'Call us or fill in the form. Tell us what happened — takes under 5 minutes.',
  },
  {
    num: '02',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="11" cy="11" r="8"/>
        <path d="M21 21l-4.35-4.35"/>
      </svg>
    ),
    title: 'We Assess',
    text: 'Photos or in-person inspection. We confirm non-fault status and arrange a courtesy car.',
  },
  {
    num: '03',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
    title: 'We Repair',
    text: 'Your car is repaired to manufacturer standard. Courtesy car provided throughout.',
  },
  {
    num: '04',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
      </svg>
    ),
    title: 'We Recover',
    text: 'Our claims partner recovers all costs from the at-fault insurer. You pay nothing.',
  },
];

const sceneItems = [
  { emoji: '📸', text: 'Photos of all vehicles, damage, number plates, and the scene' },
  { emoji: '📇', text: "Other driver's name, address, phone number, insurance details, and registration" },
  { emoji: '👥', text: 'Witness names and contact details' },
  { emoji: '👮', text: 'Police report reference number if officers attended' },
];

const faqs = [
  {
    q: 'Will I lose my No Claims Bonus?',
    a: 'No. A true non-fault claim does not affect your NCB.',
  },
  {
    q: 'Do I pay my excess?',
    a: 'Not with us. We recover it directly from the at-fault insurer.',
  },
  {
    q: 'How long does the repair take?',
    a: "It depends on the extent of the repair — we\'ll give you a clear timeframe after assessment.",
  },
  {
    q: 'What if the other driver disputes fault?',
    a: 'Our claims partner handles all fault disputes on your behalf.',
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/[0.08] bg-[#111111]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left group"
        aria-expanded={open}
      >
        <span className="font-fraunces font-light text-white text-lg group-hover:text-[#CC1A1A] transition-colors">{q}</span>
        <svg
          width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="2"
          className={`shrink-0 transition-transform duration-300 ${open ? 'rotate-45' : ''}`}
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14"/>
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="font-dm-sans text-[15px] text-white/55 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function HowNonFaultClaimsWorkPage() {
  const [formState, setFormState] = useState({ name: '', phone: '', email: '', reg: '', message: '', website: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showModal, setShowModal] = useState(false);
  const [bookingReference, setBookingReference] = useState('');
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const lastSubmitRef = React.useRef<number>(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const finalValue = name === 'reg' ? value.toUpperCase() : value;
    setFormState((prev) => ({ ...prev, [name]: finalValue }));
    if (touched[name]) {
      const err = validateCtaField(name, finalValue);
      setFieldErrors((prev) => {
        const next = { ...prev };
        if (err) next[name] = err;
        else delete next[name];
        return next;
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = validateCtaField(name, value);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  const validateCtaField = (name: string, value: string): string => {
    if (name === 'name') {
      if (!value.trim()) return 'Full name is required';
      if (value.trim().length < 2) return 'Name must be at least 2 characters';
    }
    if (name === 'phone') {
      if (!value.trim()) return 'Phone number is required';
      const stripped = value.replace(/[\s\-().]/g, '');
      if (!/^(\+44|0044|0)[1-9]\d{8,9}$/.test(stripped)) return 'Enter a valid UK phone number';
    }
    if (name === 'email' && value.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Enter a valid email address';
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ── Rate limiting ────────────────────────────────────────────────────────
    const now = Date.now();
    if (now - lastSubmitRef.current < 60000) {
      const remaining = Math.ceil((60000 - (now - lastSubmitRef.current)) / 1000);
      setRateLimitMsg(`Please wait ${remaining} seconds before submitting again.`);
      return;
    }
    setRateLimitMsg('');

    // ── Honeypot check ───────────────────────────────────────────────────────
    if (formState.website) {
      // Silently fake success for bots
      setShowModal(true);
      return;
    }

    // Validate required fields
    const allTouched = { name: true, phone: true, email: true };
    setTouched(allTouched);
    const errors: Record<string, string> = {};
    const nameErr = validateCtaField('name', formState.name);
    if (nameErr) errors.name = nameErr;
    const phoneErr = validateCtaField('phone', formState.phone);
    if (phoneErr) errors.phone = phoneErr;
    if (formState.email.trim()) {
      const emailErr = validateCtaField('email', formState.email);
      if (emailErr) errors.email = emailErr;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    setFormError('');
    lastSubmitRef.current = Date.now();

    const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    // ── Generate booking reference ────────────────────────────────────────────
    const year = new Date().getFullYear();
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const rand = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    const bookingRef = `NLB-${year}-${rand}`;

    // ── Save to Supabase (independent of email) ──────────────────────────────
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      await supabase.from('bookings').insert({
        service_type: 'non-fault-claim',
        name: formState.name,
        phone: formState.phone,
        email: formState.email || null,
        vehicle_reg: formState.reg ? formState.reg.toUpperCase() : null,
        vehicle_make_model: null,
        message: formState.message || null,
        photo_urls: null,
        status: 'new',
        reference: bookingRef,
      });
    } catch {
      // Supabase save failure must not block email
    }

    // ── Send email via Supabase Edge Function ────────────────────────────────
    try {
      const body = new FormData();
      body.append('name', formState.name);
      body.append('phone', formState.phone);
      body.append('email', formState.email || '');
      body.append('registration', formState.reg ? formState.reg.toUpperCase() : '');
      body.append('vehicleMake', '');
      body.append('damage', formState.message);
      body.append('service', 'Non-Fault Claim');
      body.append('subject', `🔔 NON-FAULT CLAIM: ${formState.name} – ${formState.reg ? formState.reg.toUpperCase() : 'No Reg'}`);
      body.append('successMessage', "Thank you! We'll call you within 1 hour to discuss your non-fault claim.");
      body.append('bookingRef', bookingRef);

      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-booking-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body,
      });
      if (!res.ok) {
        console.error('Non-fault claim email failed:', await res.text());
      }
    } catch (err) {
      console.error('Non-fault claim email error:', err);
    }

    setFormState({ name: '', phone: '', email: '', reg: '', message: '', website: '' });
    setFieldErrors({});
    setTouched({});
    setSubmitting(false);
    setBookingReference(bookingRef);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header />

      {/* ─── HERO BANNER ─── */}
      <section
        className="relative z-10 overflow-hidden"
        aria-label="Non-Fault Claims Hero Banner"
        style={{
          background: 'linear-gradient(135deg, #000000 0%, #0a0a0a 40%, #121212 100%)',
        }}
      >
        {/* Subtle diagonal texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 20px,
              rgba(255,255,255,0.012) 20px,
              rgba(255,255,255,0.012) 21px
            )`,
          }}
        />

        {/* Red radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background: 'radial-gradient(ellipse 60% 90% at 80% 50%, rgba(192,0,0,0.09) 0%, transparent 70%)',
          }}
        />

        <div className="max-w-8xl mx-auto w-full px-6 md:px-12 relative">
          <div className="flex flex-col md:flex-row items-stretch min-h-[600px] lg:min-h-[680px]">

            {/* Left column — 60% */}
            <div className="flex-1 md:w-[60%] py-24 md:py-28 lg:py-32 flex flex-col justify-center">
              {/* Eyebrow */}
              <p
                className="font-dm-sans font-bold tracking-[0.3em] uppercase mb-6"
                style={{ fontSize: '11px', color: '#C00000' }}
              >
                NON-FAULT CLAIMS
              </p>

              {/* Main Headline — massive */}
              <h1
                className="font-dm-sans font-black text-white leading-[1.0] mb-5 tracking-tight"
                style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}
              >
                Been in an accident<br />that wasn&apos;t your fault?
              </h1>

              {/* Sub-headline */}
              <p
                className="font-dm-sans font-normal mb-6 leading-relaxed"
                style={{ fontSize: 'clamp(1rem, 1.6vw, 1.2rem)', color: '#9a9a9a' }}
              >
                Here&apos;s exactly what happens next.
              </p>

              {/* Body text */}
              <p
                className="font-dm-sans font-normal mb-10 max-w-xl leading-relaxed"
                style={{ color: '#C0C0C0', fontSize: '0.95rem' }}
              >
                We guide you through every step — so you don&apos;t have to fight insurers alone. From reporting the accident to collecting your repaired car, we handle everything at zero cost to you.
              </p>

              {/* CTA Row */}
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-10">
                <a
                  href="#cta"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-white font-dm-sans font-bold text-[13px] uppercase tracking-[0.15em] transition-all duration-200 w-full sm:w-auto"
                  style={{ background: '#C00000', borderRadius: '4px' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#d40000')}
                  onMouseLeave={e => (e.currentTarget.style.background = '#C00000')}
                >
                  START YOUR FREE CLAIM →
                </a>
                <a
                  href="tel:01614001687"
                  className="inline-flex items-center justify-center gap-2.5 text-white font-dm-sans font-semibold text-[17px] tracking-wide hover:opacity-80 transition-opacity w-full sm:w-auto"
                  aria-label="Call us on 0161 400 1687"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                  0161 400 1687
                </a>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3">
                {[
                  { label: 'NO EXCESS TO PAY' },
                  { label: 'NCB PROTECTED' },
                  { label: 'FREE COURTESY CAR' },
                ].map((badge) => (
                  <div
                    key={badge.label}
                    className="inline-flex items-center gap-2 px-4 py-2.5"
                    style={{ border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.03)', borderRadius: '3px' }}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: '#C00000' }} aria-hidden="true" />
                    <span className="font-dm-sans text-[11px] font-bold text-white whitespace-nowrap tracking-[0.1em]">{badge.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column — 40% — Abstract geometric SVG */}
            <div className="hidden md:flex md:w-[40%] shrink-0 items-center justify-center relative" aria-hidden="true">
              <svg
                viewBox="0 0 480 560"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full max-w-[420px]"
                preserveAspectRatio="xMidYMid slice"
                role="img"
                aria-label="Abstract geometric composition"
              >
                {/* Large diagonal slash — primary */}
                <polygon points="480,0 480,560 320,560 80,0" fill="#0d0d0d"/>
                {/* Red diagonal accent slab */}
                <polygon points="480,0 480,180 380,0" fill="#C00000" opacity="0.9"/>
                {/* Dark grey diagonal slab */}
                <polygon points="0,560 120,560 400,0 240,0" fill="#1a1a1a"/>
                {/* Thin red diagonal line 1 */}
                <line x1="0" y1="420" x2="480" y2="80" stroke="#C00000" strokeWidth="2" opacity="0.7"/>
                {/* Thin red diagonal line 2 */}
                <line x1="0" y1="460" x2="480" y2="120" stroke="#C00000" strokeWidth="1" opacity="0.4"/>
                {/* Thin red diagonal line 3 */}
                <line x1="0" y1="380" x2="480" y2="40" stroke="#C00000" strokeWidth="0.5" opacity="0.3"/>
                {/* Large triangle — top right */}
                <polygon points="480,0 480,320 200,0" fill="#111111" opacity="0.8"/>
                {/* Overlapping triangle — mid */}
                <polygon points="480,200 480,480 120,480" fill="#161616" opacity="0.6"/>
                {/* Red accent triangle — corner */}
                <polygon points="480,380 480,560 280,560" fill="#C00000" opacity="0.15"/>
                {/* Bright red accent triangle — top */}
                <polygon points="380,0 480,0 480,100" fill="#C00000" opacity="0.6"/>
                {/* Grey accent shape */}
                <polygon points="0,0 120,0 360,560 240,560" fill="#222222" opacity="0.4"/>
                {/* Thin white diagonal lines */}
                <line x1="60" y1="0" x2="300" y2="560" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
                <line x1="120" y1="0" x2="360" y2="560" stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
                {/* Red glow spot */}
                <ellipse cx="400" cy="140" rx="80" ry="60" fill="rgba(192,0,0,0.12)" style={{filter:'blur(20px)'}}/>
                {/* Small red accent rectangle */}
                <rect x="340" y="240" width="140" height="3" fill="#C00000" opacity="0.8" transform="rotate(-52, 340, 240)"/>
                <rect x="360" y="260" width="120" height="1.5" fill="#C00000" opacity="0.5" transform="rotate(-52, 360, 260)"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Bottom red divider */}
        <div className="w-full h-[2px]" style={{ background: 'linear-gradient(90deg, #C00000 0%, rgba(192,0,0,0.4) 50%, transparent 100%)' }} aria-hidden="true" />
      </section>

      {/* ─── TRUSTPILOT REVIEWS CAROUSEL ─── */}
      <div style={{ background: '#121212', padding: '48px 24px' }}>
        <div className="max-w-8xl mx-auto">
          <TrustpilotWidget templateId="53aa8912dec7e10d38f59f36" height="140px" />
        </div>
      </div>

      {/* ─── SECTION 1: What is a non-fault claim? ─── */}
      <section className="bg-[#0d0d0d] border-t border-white/[0.06] py-20 px-6 md:px-12">
        <div className="max-w-8xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">

          {/* Left — text */}
          <div>
            <p className="font-dm-sans font-bold tracking-[0.3em] uppercase text-[#C00000] mb-5" style={{ fontSize: '11px' }}>
              01 — WHAT IS A NON-FAULT CLAIM
            </p>
            <h2 className="font-dm-sans font-black text-white leading-[1.1] mb-6" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)' }}>
              If it wasn&apos;t your fault,<br />you shouldn&apos;t pay a penny.
            </h2>
            <p className="font-dm-sans text-[15px] leading-relaxed mb-8" style={{ color: '#C0C0C0' }}>
              A non-fault claim means someone else caused the accident. Their insurance should pay — not yours.
              This includes rear-end collisions, being hit while parked, and someone pulling out on you.
            </p>
            {/* Bullet points with red checkmarks */}
            <ul className="space-y-4">
              {[
                'Your No Claims Bonus is protected',
                'You pay zero excess',
                "Claim goes against other driver's policy",
              ].map((point, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center"
                    style={{ background: 'rgba(192,0,0,0.15)', border: '1px solid rgba(192,0,0,0.5)', borderRadius: '3px' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#C00000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </span>
                  <span className="font-dm-sans text-[15px] font-medium text-white/85">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — AI-generated abstract automotive image */}
          <div className="relative flex items-center justify-center overflow-hidden" style={{ borderRadius: '4px', minHeight: '360px', aspectRatio: '5/4' }}>
            <Image
              src="/assets/images/non_fault_abstract.png"
              alt="Premium abstract automotive composition representing a non-fault claim scenario"
              width={600}
              height={480}
              className="w-full h-full object-cover"
              style={{ borderRadius: '4px' }}
              priority
            />
            {/* Subtle overlay for brand consistency */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.15) 0%, transparent 60%)', borderRadius: '4px' }}
              aria-hidden="true"
            />
          </div>
        </div>
      </section>

      {/* ─── SECTION 2: Your 3 options ─── */}
      <section className="bg-[#0a0a0a] border-t border-white/[0.06] py-20 px-6 md:px-12">
        <div className="max-w-8xl mx-auto">
          <p className="font-dm-sans font-bold tracking-[0.3em] uppercase text-[#C00000] mb-4" style={{ fontSize: '11px' }}>
            02 — YOUR 3 OPTIONS
          </p>
          <h2 className="font-fraunces font-light text-white text-3xl md:text-4xl leading-tight mb-10">
            Your 3 options after an accident
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" role="table">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left py-4 px-5 font-dm-sans text-[11px] font-semibold tracking-[0.2em] uppercase text-white/40">Option</th>
                  <th className="text-left py-4 px-5 font-dm-sans text-[11px] font-semibold tracking-[0.2em] uppercase text-white/40">What Happens</th>
                  <th className="text-left py-4 px-5 font-dm-sans text-[11px] font-semibold tracking-[0.2em] uppercase text-white/40">Pros</th>
                  <th className="text-left py-4 px-5 font-dm-sans text-[11px] font-semibold tracking-[0.2em] uppercase text-white/40">Cons</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/[0.06] bg-[#111111]">
                  <td className="py-5 px-5 font-fraunces font-light text-white text-[15px] min-w-[160px]">Claim on your own insurance</td>
                  <td className="py-5 px-5 font-dm-sans text-[14px] text-white/55 min-w-[200px]">Your insurer pays, then tries to recover costs</td>
                  <td className="py-5 px-5 font-dm-sans text-[14px] text-white/55 min-w-[160px]">Quick fix</td>
                  <td className="py-5 px-5 font-dm-sans text-[14px] text-white/55 min-w-[220px]">Pay excess upfront, NCB at risk, premiums may rise</td>
                </tr>
                <tr className="border-b border-white/[0.06] bg-[#111111]">
                  <td className="py-5 px-5 font-fraunces font-light text-white text-[15px]">Claim directly against third-party insurer</td>
                  <td className="py-5 px-5 font-dm-sans text-[14px] text-white/55">You contact the other driver&apos;s insurer yourself</td>
                  <td className="py-5 px-5 font-dm-sans text-[14px] text-white/55 min-w-[160px]">No impact on your policy</td>
                  <td className="py-5 px-5 font-dm-sans text-[14px] text-white/55 min-w-[220px]">Slow, complicated, insurers make it difficult</td>
                </tr>
                {/* Highlighted recommended row */}
                <tr className="bg-[#1a0a0a] border border-[#CC1A1A]/40">
                  <td className="py-5 px-5 min-w-[160px]">
                    <span className="font-fraunces font-semibold text-[#CC1A1A] text-[15px]">Use our non-fault service</span>
                    <span className="block mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-[#CC1A1A] text-white font-dm-sans text-[9px] font-bold uppercase tracking-widest w-fit">Recommended</span>
                  </td>
                  <td className="py-5 px-5 font-dm-sans text-[14px] text-white/70">We handle everything through our claims partner</td>
                  <td className="py-5 px-5 font-dm-sans text-[14px] text-white/70">You pay £0, NCB protected, courtesy car provided, we handle all paperwork</td>
                  <td className="py-5 px-5 font-dm-sans text-[14px] text-[#CC1A1A] font-semibold">Nothing — it&apos;s the best option</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ─── SECTION 3: Excess question ─── */}
      <section className="bg-[#0d0d0d] border-t border-white/[0.06] py-20 px-6 md:px-12">
        <div className="max-w-8xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-dm-sans font-bold tracking-[0.3em] uppercase text-[#C00000] mb-4" style={{ fontSize: '11px' }}>
              03 — EXCESS QUESTION
            </p>
            <h2 className="font-fraunces font-light text-white text-3xl md:text-4xl leading-tight mb-6">
              Do I have to pay my excess?
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-white/50 text-[10px] font-bold">✗</span>
                </div>
                <p className="font-dm-sans text-[15px] text-white/55 leading-relaxed">
                  <strong className="text-white/80">If you claim on your own policy first:</strong> yes, you pay upfront (but may be able to reclaim later).
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#CC1A1A]/20 border border-[#CC1A1A]/40 flex items-center justify-center shrink-0 mt-0.5">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <p className="font-dm-sans text-[15px] text-white/55 leading-relaxed">
                  <strong className="text-white/80">If you use our service:</strong> no — we recover it directly from the at-fault insurer. You pay £0.
                </p>
              </div>
            </div>
          </div>
          {/* £0 badge visual */}
          <div className="flex items-center justify-center">
            <div className="relative flex flex-col items-center justify-center w-56 h-56 border-2 border-[#CC1A1A]/40 bg-[#111111] rounded-full">
              <div className="absolute inset-3 border border-[#CC1A1A]/20 rounded-full" />
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" className="mb-2" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span className="font-fraunces font-light text-5xl text-white leading-none">£0</span>
              <span className="font-dm-sans text-[11px] font-bold uppercase tracking-[0.2em] text-[#CC1A1A] mt-2">Your Excess</span>
              <span className="font-dm-sans text-[10px] text-white/35 mt-1">Protected by us</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECTION 4: Will this affect my insurance? ─── */}
      <section className="bg-[#0a0a0a] border-t border-white/[0.06] py-20 px-6 md:px-12">
        <div className="max-w-8xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-dm-sans font-bold tracking-[0.3em] uppercase text-[#C00000] mb-4" style={{ fontSize: '11px' }}>
              04 — INSURANCE PROTECTION
            </p>
            <h2 className="font-fraunces font-light text-white text-3xl md:text-4xl leading-tight mb-6">
              Will this affect my insurance?
            </h2>
            <p className="font-dm-sans text-[15px] text-white/55 leading-relaxed">
              When we handle it as a true non-fault claim through our claims partner, your No Claims Bonus is
              fully protected. The claim is registered against the at-fault driver&apos;s policy — not yours.
            </p>
          </div>
          {/* NCB Protected badge */}
          <div className="flex items-center justify-center">
            <div className="relative flex flex-col items-center justify-center w-56 h-56 border-2 border-green-500/40 bg-[#0a1a0a] rounded-full">
              <div className="absolute inset-3 border border-green-500/20 rounded-full" />
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="1.5" className="mb-2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span className="font-fraunces font-light text-2xl text-white leading-none text-center">NCB</span>
              <span className="font-fraunces font-light text-2xl text-white leading-none text-center">Protected</span>
              <span className="font-dm-sans text-[10px] text-green-400/70 mt-2 tracking-widest uppercase">✓ Fully Secured</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECTION 5: What you're entitled to ─── */}
      <section className="bg-[#0d0d0d] border-t border-white/[0.06] py-20 px-6 md:px-12">
        <div className="max-w-8xl mx-auto">
          <p className="font-dm-sans font-bold tracking-[0.3em] uppercase text-[#C00000] mb-4" style={{ fontSize: '11px' }}>
            05 — WHAT YOU'RE ENTITLED TO
          </p>
          <h2 className="font-fraunces font-light text-white text-3xl md:text-4xl leading-tight mb-10">
            What you&apos;re entitled to
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {entitlements.map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-[#111111] border border-white/[0.07] p-5">
                <span className="text-2xl shrink-0" role="img" aria-hidden="true">{item.emoji}</span>
                <span className="font-dm-sans text-[14px] text-white/65 leading-relaxed">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 6: Step-by-step process ─── */}
      <section className="bg-[#0a0a0a] border-t border-white/[0.06] py-20 px-6 md:px-12">
        <div className="max-w-8xl mx-auto">
          <p className="font-dm-sans font-bold tracking-[0.3em] uppercase text-[#C00000] mb-4" style={{ fontSize: '11px' }}>
            06 — OUR PROCESS
          </p>
          <h2 className="font-fraunces font-light text-white text-3xl md:text-4xl leading-tight mb-10">
            Our step-by-step process
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="bg-[#111111] border border-white/[0.07] p-6 flex flex-col gap-4 relative overflow-hidden">
                <div className="absolute top-4 right-4 font-fraunces font-light text-5xl text-white/[0.04] leading-none select-none" aria-hidden="true">{step.num}</div>
                <div className="w-12 h-12 bg-[#1a0a0a] border border-[#CC1A1A]/30 flex items-center justify-center text-[#CC1A1A]">
                  {step.icon}
                </div>
                <div>
                  <span className="block font-dm-sans text-[10px] font-bold uppercase tracking-[0.25em] text-[#CC1A1A] mb-2">Step {step.num}</span>
                  <h3 className="font-fraunces font-light text-white text-xl mb-2">{step.title}</h3>
                  <p className="font-dm-sans text-[13px] text-white/50 leading-relaxed">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 7: What to collect at the scene ─── */}
      <section className="bg-[#0d0d0d] border-t border-white/[0.06] py-20 px-6 md:px-12">
        <div className="max-w-8xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <div>
            <p className="font-dm-sans font-bold tracking-[0.3em] uppercase text-[#C00000] mb-4" style={{ fontSize: '11px' }}>
              07 — SCENE COLLECTION
            </p>
            <h2 className="font-fraunces font-light text-white text-3xl md:text-4xl leading-tight mb-8">
              What to collect at the scene
            </h2>
            <div className="space-y-4 mb-8">
              {sceneItems.map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="text-xl shrink-0 mt-0.5" role="img" aria-hidden="true">{item.emoji}</span>
                  <span className="font-dm-sans text-[14px] text-white/60 leading-relaxed">{item.text}</span>
                </div>
              ))}
            </div>
            {/* Warning box */}
            <div className="border border-[#CC1A1A]/50 bg-[#1a0a0a] px-5 py-4">
              <div className="flex items-start gap-3">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="2" className="shrink-0 mt-0.5" aria-hidden="true">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <p className="font-dm-sans text-[13px] text-[#CC1A1A] leading-relaxed font-medium">
                  Never admit fault at the scene — even saying &apos;sorry&apos; can be used against you.
                </p>
              </div>
            </div>
          </div>
          {/* Placeholder image slot */}
          <div className="w-full overflow-hidden rounded-sm border border-white/[0.08]" style={{ aspectRatio: '7/4' }}>
            <Image
              src="/assets/images/what_to_collect_scene.png"
              alt="Illustration showing what information to collect at an accident scene including photos and documents"
              width={700}
              height={400}
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* ─── SECTION 8: FAQ ─── */}
      <section className="bg-[#0a0a0a] border-t border-white/[0.06] py-20 px-6 md:px-12">
        <div className="max-w-8xl mx-auto">
          <p className="font-dm-sans font-bold tracking-[0.3em] uppercase text-[#C00000] mb-4" style={{ fontSize: '11px' }}>
            08 — COMMON QUESTIONS
          </p>
          <h2 className="font-fraunces font-light text-white text-3xl md:text-4xl leading-tight mb-10">
            Common questions
          </h2>
          <div className="max-w-3xl space-y-2">
            {faqs.map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA SECTION ─── */}
      <section id="cta" className="relative z-10 bg-[#0d0d0d] border-t border-white/[0.06] py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-8xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-0 min-h-[500px]">
          {/* Left — messaging */}
          <div className="border-b md:border-b-0 md:border-r border-white/[0.06] pb-12 md:pb-0 md:pr-16 flex flex-col justify-between">
            <div>
              <h2 className="font-fraunces font-light text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
                Ready to start<br />
                <span className="italic text-[#CC1A1A]">your claim?</span>
              </h2>
              <p className="font-dm-sans text-[15px] text-white/40 max-w-md leading-relaxed mb-10">
                Contact us now and we&apos;ll take it from there.
              </p>
              <div className="flex flex-col gap-4 mb-10">
                {['You pay £0 — no excess, no hidden charges.', 'NCB fully protected.', 'Courtesy car provided.', 'We handle all paperwork and insurer negotiations.'].map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                      <path d="M20 6 9 17 4 12"/>
                    </svg>
                    <span className="font-dm-sans text-[14px] text-white/55">{point}</span>
                  </div>
                ))}
              </div>
              <a href="tel:01614001687" className="inline-flex items-center gap-3 text-[#CC1A1A] hover:text-[#E02020] transition-colors group">
                <div className="w-12 h-12 rounded-full border border-[#CC1A1A]/30 flex items-center justify-center group-hover:bg-[#CC1A1A]/10 transition-colors">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 026 6l1.27-1.27a2 2 0 022.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0222 16.92z"/>
                  </svg>
                </div>
                <div>
                  <span className="block font-fraunces font-light text-2xl tracking-tight">0161 400 1687</span>
                  <span className="block font-dm-sans text-[11px] uppercase tracking-[0.2em] text-white/30">Call us, 8am–7pm Mon–Sat</span>
                </div>
              </a>
              <a href="mailto:contactus@nolimitauto.co.uk" className="inline-flex items-center gap-2 mt-4 text-white/40 hover:text-white/70 transition-colors font-dm-sans text-[13px]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                contactus@nolimitauto.co.uk
              </a>
            </div>
          </div>

          {/* Right — form */}
          <div className="pt-12 md:pt-0 md:pl-16 flex flex-col justify-center">
            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center gap-6 py-12">
                <div className="w-16 h-16 rounded-full bg-[#CC1A1A]/15 border border-[#CC1A1A]/40 flex items-center justify-center">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h3 className="font-fraunces font-light italic text-3xl text-white">Claim request received.</h3>
                <p className="font-dm-sans text-[15px] text-white/45 max-w-sm leading-relaxed">
                  We&apos;ll call you back within 2 hours during business hours to get everything started.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8" noValidate>
                <div>
                  <label className="block font-dm-sans text-[10px] font-medium uppercase tracking-[0.25em] text-white/35 mb-2" htmlFor="cta-name">Your Name <span className="text-[#CC1A1A]">*</span></label>
                  <input id="cta-name" name="name" type="text" required autoComplete="name" placeholder="e.g. James Whitfield" value={formState.name} onChange={handleChange} onBlur={handleBlur}
                    className={`w-full bg-transparent border-b py-4 text-white font-fraunces font-light text-xl placeholder:text-white/20 focus:border-[#CC1A1A] transition-colors outline-none ${touched.name && fieldErrors.name ? 'border-red-500' : 'border-white/15'}`} />
                  {touched.name && fieldErrors.name && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block font-dm-sans text-[10px] font-medium uppercase tracking-[0.25em] text-white/35 mb-2" htmlFor="cta-phone">Phone Number <span className="text-[#CC1A1A]">*</span></label>
                  <input id="cta-phone" name="phone" type="tel" required autoComplete="tel" placeholder="07700 900 000" value={formState.phone} onChange={handleChange} onBlur={handleBlur}
                    className={`w-full bg-transparent border-b py-4 text-white font-fraunces font-light text-xl placeholder:text-white/20 focus:border-[#CC1A1A] transition-colors outline-none ${touched.phone && fieldErrors.phone ? 'border-red-500' : 'border-white/15'}`} />
                  {touched.phone && fieldErrors.phone && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.phone}</p>
                  )}
                </div>
                <div>
                  <label className="block font-dm-sans text-[10px] font-medium uppercase tracking-[0.25em] text-white/35 mb-2" htmlFor="cta-email">Email Address <span className="text-white/25 normal-case tracking-normal">(optional — for confirmation)</span></label>
                  <input id="cta-email" name="email" type="email" autoComplete="email" placeholder="your@email.com" value={formState.email} onChange={handleChange} onBlur={handleBlur}
                    className={`w-full bg-transparent border-b py-4 text-white font-fraunces font-light text-xl placeholder:text-white/20 focus:border-[#CC1A1A] transition-colors outline-none ${touched.email && fieldErrors.email ? 'border-red-500' : 'border-white/15'}`} />
                  {touched.email && fieldErrors.email && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block font-dm-sans text-[10px] font-medium uppercase tracking-[0.25em] text-white/35 mb-2" htmlFor="cta-reg">Vehicle Registration</label>
                  <input id="cta-reg" name="reg" type="text" autoComplete="off" placeholder="e.g. AB71 XYZ" value={formState.reg} onChange={handleChange}
                    className="w-full bg-transparent border-b border-white/15 py-4 text-white font-fraunces font-light text-xl placeholder:text-white/20 focus:border-[#CC1A1A] transition-colors outline-none uppercase" />
                </div>
                <div>
                  <label className="block font-dm-sans text-[10px] font-medium uppercase tracking-[0.25em] text-white/35 mb-2" htmlFor="cta-message">Brief description of accident</label>
                  <textarea id="cta-message" name="message" rows={2} placeholder="Rear-ended at traffic lights on 07/03/2026..." value={formState.message} onChange={handleChange}
                    className="w-full bg-transparent border-b border-white/15 py-4 text-white font-fraunces font-light text-xl placeholder:text-white/20 focus:border-[#CC1A1A] transition-colors outline-none resize-none" />
                </div>
                <div className="pt-4">
                  {formError && (
                    <p className="font-dm-sans text-[13px] text-red-400 mb-4">{formError}</p>
                  )}
                  {rateLimitMsg && (
                    <p className="font-dm-sans text-[13px] text-yellow-400 mb-4">{rateLimitMsg}</p>
                  )}
                  <button type="submit" disabled={submitting}
                    className="group flex items-center justify-between w-full border border-[#CC1A1A]/40 hover:bg-[#CC1A1A] hover:border-[#CC1A1A] transition-all duration-300 rounded-none p-6 disabled:opacity-60 disabled:cursor-not-allowed">
                    <span className="font-dm-sans text-[11px] font-medium uppercase tracking-[0.25em] text-white group-hover:text-[#111111] transition-colors">
                      {submitting ? 'Sending…' : 'Start Your Free Claim'}
                    </span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-[#CC1A1A] group-hover:text-[#111111] transition-colors" aria-hidden="true">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
                {/* Honeypot — hidden from real users, visible to bots */}
                <div
                  style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                  aria-hidden="true"
                >
                  <label htmlFor="website-hp-nfc">Website</label>
                  <input
                    id="website-hp-nfc"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formState.website}
                    onChange={e => setFormState(prev => ({ ...prev, website: e.target.value }))}
                  />
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ─── SUCCESS MODAL ─── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-heading"
        >
          <div
            className="relative w-full max-w-md rounded-sm p-8 flex flex-col items-center text-center"
            style={{ background: '#121212', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {/* Animated checkmark */}
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ background: 'rgba(22,163,74,0.15)', border: '2px solid rgba(22,163,74,0.5)' }}
              aria-hidden="true"
            >
              <svg
                width="40" height="40" viewBox="0 0 24 24" fill="none"
                stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: 'checkPop 0.4s ease-out' }}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 id="modal-heading" className="font-dm-sans font-black text-white text-2xl mb-3">
              Booking Received!
            </h2>
            <p className="font-dm-sans text-white/60 text-[15px] leading-relaxed mb-4">
              We&apos;ll call you within 1 hour to discuss your non-fault claim.
            </p>
            {bookingReference && (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-sm"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <span className="font-dm-sans text-[11px] uppercase tracking-[0.2em] text-white/35">Reference</span>
                <span className="font-dm-sans text-[13px] font-bold text-white/80 tracking-wider">{bookingReference}</span>
              </div>
            )}
            <button
              onClick={handleCloseModal}
              className="w-full py-3 font-dm-sans font-bold text-[13px] uppercase tracking-[0.15em] text-white transition-all duration-200 rounded-sm"
              style={{ background: '#CC1A1A' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
              onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <style>{`@keyframes checkPop { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }`}</style>

      <Footer />
    </div>
  );
}
